import React from "react";
import { StyleSheet, Text, TouchableOpacity, View, Image } from "react-native";
import { colors } from "src/assets";
import { IPreConversation } from "src/domains";
import { stringDateFullDynamicFormat } from "src/utils/date";
import { Icon } from "src/components";
import { UserStatus } from "src/domains";
import { trim } from "src/utils/string";

type iconProps = {
  color: string;
};

const TriangleIcon = ({ color }: iconProps) => {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20">
      <path d="M20,136,0,156V136Z" transform="translate(0 -136)" fill={color} />
    </svg>
  );
};

const ChevronIcon = ({ color }: iconProps) => {
  return (
    <svg width="5.972" height="10" viewBox="0 0 5.972 10">
      <path
        d="M673.227,1210.877a.887.887,0,0,1,.629.26l4.193,4.193a.889.889,0,0,1,0,1.258l-4.028,4.028a.889.889,0,1,1-1.258-1.257l3.4-3.4-3.564-3.564a.889.889,0,0,1,.629-1.518Z"
        transform="translate(-672.338 -1210.877)"
        fill={color}
      />
    </svg>
  );
};

export type ChatHistoryItemProps = {
  cognitoUserId: string;
  item: IPreConversation;
  color: "white" | "transparent";
  onPress?: () => void;
};

export const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({
  color,
  cognitoUserId,
  item,
  onPress,
}) => {
  let avatarStyle = {};
  let containerStyle = {};
  let userName: string = "";
  let userImageUrl: string | undefined;
  let backgroundColor: string = color;
  if (item.sender?.id === cognitoUserId) {
    userName = item.opponent?.name || "Unknown";
    userImageUrl = item.opponent?.image;
    avatarStyle = {
      ...(item.opponent?.status === UserStatus.LOGOUT
        ? {
            borderColor: colors.chokuGray6,
          }
        : {}),
      ...(item.opponent?.status === UserStatus.OFFERABLE
        ? {
            borderColor: colors.chokuGreen,
          }
        : {}),
      ...(item.opponent?.status === UserStatus.ACTIVE
        ? {
            borderColor: colors.orange,
          }
        : {}),
      ...(item.opponent?.status === UserStatus.IN_CALL
        ? {
            borderColor: colors.chokuRed,
          }
        : {}),
    };
  } else {
    userName = item.sender?.name || "Unknown";
    userImageUrl = item.sender?.image;
    avatarStyle = {
      ...(item.sender?.status === UserStatus.LOGOUT
        ? {
            borderColor: colors.chokuGray6,
          }
        : {}),
      ...(item.sender?.status === UserStatus.OFFERABLE
        ? {
            borderColor: colors.chokuGreen,
          }
        : {}),
      ...(item.sender?.status === UserStatus.ACTIVE
        ? {
            borderColor: colors.orange,
          }
        : {}),
      ...(item.sender?.status === UserStatus.IN_CALL
        ? {
            borderColor: colors.chokuRed,
          }
        : {}),
    };
  }

  containerStyle = { ...containerStyle, backgroundColor };
  return (
    <TouchableOpacity
      style={[styles.container, containerStyle]}
      onPress={onPress}
    >
      <View style={styles.content}>
        {item.lastReceivedMessage?.readAt === "0" ? (
          <View style={styles.triangle}>
            <TriangleIcon color={"#00e5e1"} />
          </View>
        ) : null}
        <View style={styles.contentCol2}>
          <Text
            style={[
              styles.message,
              item.lastReceivedMessage?.readAt === "0"
                ? styles.messageRead
                : {},
            ]}
          >
            {trim(item.service?.title || "", 32, "...")}
          </Text>
          <View style={styles.userContainer}>
            {userImageUrl ? (
              <Image
                style={[styles.avatar, avatarStyle]}
                source={{ uri: userImageUrl }}
              />
            ) : (
              <View style={[styles.avatar, styles.avatarEmpty, avatarStyle]}>
                <Icon name={"person"} color={colors.chokuGray5} size={10} />
              </View>
            )}
            <Text style={styles.username}>
              {trim(userName || "", 32, "...")}
              さん
            </Text>
          </View>
        </View>
        <View style={styles.timeContainer}>
          {item.lastReceivedMessage?.createdAt ? (
            <Text style={styles.time}>
              {stringDateFullDynamicFormat(item.lastReceivedMessage?.createdAt)}
            </Text>
          ) : null}
        </View>
        <View style={styles.chevron}>
          <ChevronIcon color={"#a5a5a5"} />
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
  },
  containerWhite: {
    backgroundColor: colors.white,
  },
  content: {
    flexDirection: "row",
  },
  contentCol2: {
    flex: 1,
    paddingVertical: 16,
    paddingLeft: 16,
  },
  userContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 10,
  },
  avatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.gray1,
  },
  avatarEmpty: {
    backgroundColor: colors.gray1,
    justifyContent: "center",
    alignItems: "center",
  },
  username: {
    fontSize: 12,
    color: colors.chokuGray5,
    marginLeft: 8,
  },
  message: {
    fontSize: 14,
    fontWeight: "500",
    color: colors.chokuGray5,
    lineHeight: 22,
  },
  messageRead: {
    color: colors.chokuBlack,
    fontWeight: "700",
  },
  timeContainer: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginLeft: 16,
  },
  time: {
    fontSize: 12,
    fontWeight: "500",
    color: colors.chokuGray5,
  },
  chevron: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: 30,
  },
  triangle: {
    position: "absolute",
    left: 0,
    top: 0,
  },
});
